import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { resolveInviteUrl } from '../lib/invite-url';
import type { PracticeContext } from '../lib/types';

export default function Settings({ context }: { context: PracticeContext }) {
  const qc = useQueryClient();
  const p = context.practice;
  const [displayName, setDisplayName] = useState(p.displayName);
  const [doctorName, setDoctorName] = useState(p.doctorName ?? '');
  const [crm, setCrm] = useState(p.crm ?? '');
  const [primaryColor, setPrimaryColor] = useState(p.primaryColor ?? '#22c55e');
  const [logoUrl, setLogoUrl] = useState(p.logoUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    setErr(null);

    const { error } = await supabase
      .from('practices')
      .update({
        display_name: displayName.trim(),
        doctor_name: doctorName.trim() || null,
        crm: crm.trim() || null,
        primary_color: primaryColor,
        logo_url: logoUrl.trim() || null,
      })
      .eq('id', p.id);

    setSaving(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setMsg('Salvo. Atualize a página se a cor não mudar no menu.');
    void qc.invalidateQueries();
  }

  const landingInvite = resolveInviteUrl(p.inviteUrl);

  return (
    <div>
      <h1 className="h3 mb-1">Consultório</h1>
      <p className="text-muted small mb-4">
        Marca e dados exibidos no convite <code>/c/{p.slug}</code> e neste painel.
      </p>

      <div className="row g-4">
        <div className="col-lg-7">
          <form onSubmit={onSave} className="card shadow-sm">
            <div className="card-body">
              <h2 className="h6 mb-3">Identidade</h2>

              <div className="mb-3">
                <label className="form-label small">Nome do consultório</label>
                <input
                  className="form-control"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small">Nome da pediatra</label>
                  <input
                    className="form-control"
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">CRM</label>
                  <input
                    className="form-control"
                    value={crm}
                    onChange={(e) => setCrm(e.target.value)}
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small">Cor principal</label>
                  <div className="d-flex gap-2">
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                    />
                    <input
                      className="form-control"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label small">URL do logo</label>
                  <input
                    className="form-control"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="https://…"
                  />
                </div>
              </div>

              {err && <div className="alert alert-danger small">{err}</div>}
              {msg && <div className="alert alert-success small">{msg}</div>}

              <button type="submit" className="btn btn-bb" disabled={saving}>
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>

        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <h2 className="h6 mb-3">Preview</h2>
              <div
                className="rounded-3 p-3 text-white mb-3"
                style={{ background: primaryColor }}
              >
                {logoUrl ? (
                  <img src={logoUrl} alt="" height={32} className="mb-2 rounded" />
                ) : null}
                <div className="fw-bold">{displayName}</div>
                {doctorName && <div className="small opacity-75">{doctorName}</div>}
              </div>

              <h2 className="h6 mb-2">Link de convite</h2>
              <p className="small text-muted">Slug fixo: <code>{p.slug}</code></p>
              <div className="input-group input-group-sm">
                <input className="form-control" readOnly value={landingInvite} />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => void navigator.clipboard.writeText(landingInvite)}
                >
                  Copiar
                </button>
              </div>
              <a
                href={landingInvite}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline-success mt-3"
              >
                Abrir página de convite
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
